import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Card,
  Tooltip,
  Form,
  DatePicker,
  InputNumber,
  Divider,
  Drawer,
  Switch,
  message,
  Empty,
  Avatar,
  Tag,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  SwapOutlined,
  InboxOutlined,
  ToolOutlined,
  DeleteOutlined,
  DollarOutlined,
  TransactionOutlined,
  UserOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { getUserList } from "../api/user";
import { getTransactions, updateTransaction, getAssets } from "../api/asset";
import debounce from "lodash.debounce";
import HostSelect from "../components/host/HostSelect";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

// ─── 操作类型配置 ─────────────────────────────────────────────────────────────
const OPERATION_CONFIG = {
  inbound: {
    text: "入库",
    icon: <InboxOutlined />,
    color: "#10b981",
    bg: "#ecfdf5",
    border: "#a7f3d0",
  },
  checkout: {
    text: "领用",
    icon: <UserOutlined />,
    color: "#3b82f6",
    bg: "#eff6ff",
    border: "#bfdbfe",
  },
  transfer: {
    text: "转移",
    icon: <SwapOutlined />,
    color: "#8b5cf6",
    bg: "#f5f3ff",
    border: "#ddd6fe",
  },
  repair: {
    text: "维修",
    icon: <ToolOutlined />,
    color: "#f59e0b",
    bg: "#fffbeb",
    border: "#fde68a",
  },
  scrap: {
    text: "报废",
    icon: <DeleteOutlined />,
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
  },
  settle: {
    text: "结算",
    icon: <DollarOutlined />,
    color: "#06b6d4",
    bg: "#ecfeff",
    border: "#a5f3fc",
  },
};
const operation_count_default = {
  inbound: 0,
  checkout: 0,
  transfer: 0,
  repair: 0,
  scrap: 0,
  settle: 0,
};

// ─── Mock 数据（对接真实接口时删除） ──────────────────────────────────────────
const mockUsers = [
  { _id: "u1", name: "张伟" },
  { _id: "u2", name: "李娜" },
  { _id: "u3", name: "王芳" },
];
const mockHosts = [
  { _id: "h1", roomNumber: "A101", building: "A栋" },
  { _id: "h2", roomNumber: "A102", building: "A栋" },
  { _id: "h3", roomNumber: "B201", building: "B栋" },
];
const mockAssets = [
  { _id: "a1", assetNo: "ASSET-2024-0001", assetName: "ThinkPad笔记本" },
  { _id: "a2", assetNo: "ASSET-2024-0002", assetName: "办公椅" },
  { _id: "a3", assetNo: "ASSET-2024-0003", assetName: "显示器" },
];

// ─── 小组件：操作类型标签 ──────────────────────────────────────────────────────
function OperationTag({ operation }) {
  const cfg = OPERATION_CONFIG[operation];
  if (!cfg) return <Text type="secondary">{operation || "—"}</Text>;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 9px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
      }}
    >
      <span style={{ fontSize: 10 }}>{cfg.icon}</span>
      {cfg.text}
    </span>
  );
}

// ─── 小组件：结算状态标签 ──────────────────────────────────────────────────────
function SettleTag({ needSettle, settled, amount }) {
  if (!needSettle)
    return <Text style={{ fontSize: 11, color: "#cbd5e1" }}>无需结算</Text>;
  return settled ? (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        color: "#10b981",
        background: "#ecfdf5",
        border: "1px solid #a7f3d0",
      }}
    >
      <CheckCircleOutlined style={{ fontSize: 10 }} />
      已结算{amount != null ? ` ¥${Number(amount).toLocaleString()}` : ""}
    </span>
  ) : (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        color: "#f59e0b",
        background: "#fffbeb",
        border: "1px solid #fde68a",
      }}
    >
      <ClockCircleOutlined style={{ fontSize: 10 }} />
      待结算{amount != null ? ` ¥${Number(amount).toLocaleString()}` : ""}
    </span>
  );
}

// ─── 箭头变更展示 ─────────────────────────────────────────────────────────────
function ChangeCell({ from, to, icon }) {
  if (!from && !to) return <Text style={{ color: "#cbd5e1" }}>—</Text>;
  return (
    <Space size={3} style={{ fontSize: 11 }}>
      {from ? (
        <Text style={{ fontSize: 11, color: "#94a3b8" }}>{from}</Text>
      ) : (
        <Text style={{ color: "#e2e8f0" }}>—</Text>
      )}
      <span style={{ color: "#cbd5e1", fontSize: 10 }}>→</span>
      {to ? (
        <Text style={{ fontSize: 11, color: "#0f172a", fontWeight: 500 }}>
          {to}
        </Text>
      ) : (
        <Text style={{ color: "#e2e8f0" }}>—</Text>
      )}
    </Space>
  );
}

// ─── 编辑 Drawer ──────────────────────────────────────────────────────────────
function EditDrawer({ open, record, userOptions, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [needSettle, setNeedSettle] = useState(false);
  const [assetOptions, setAssetOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  function initOptions(record) {
    const assetOptions = [
      {
        value: record.asset._id,
        label: record.asset.assetNo,
      },
    ];
    const userids = new Set();
    const userOptions = [];
    if (record?.fromOwner) {
      !userids.has(record.fromOwner._id) &&
        userids.add(record.fromOwner._id) &&
        userOptions.push({
          value: record.fromOwner._id,
          label: record.fromOwner.name,
        });
    }
    if (record?.toOwner) {
      !userids.has(record.toOwner._id) &&
        userids.add(record.toOwner._id) &&
        userOptions.push({
          value: record.toOwner._id,
          label: record.toOwner.name,
        });
    }
    if (record?.operator) {
      !userids.has(record.operator._id) &&
        userids.add(record.operator._id) &&
        userOptions.push({
          value: record.operator._id,
          label: record.operator.name,
        });
    }
    const hostIds = new Set();

    return { assetOptions };
  }
  useEffect(() => {
    if (open && record) {
      const { assetOptions } = initOptions(record);
      setAssetOptions(assetOptions);
      form.setFieldsValue({
        ...record,
        operator: record.operator?._id,
        fromRoom: record.fromRoom?._id,
        toRoom: record.toRoom?._id,
        fromOwner: record.fromOwner?._id,
        toOwner: record.toOwner?._id,
        operationTime: record.operationTime
          ? dayjs(record.operationTime)
          : null,
        settleTime: record.settleTime ? dayjs(record.settleTime) : null,
      });
      setNeedSettle(!!record.needSettle);
    } else if (!open) {
      // setUserOptions([]);
      // setHostOptions([]);
      setAssetOptions([]);
    }
  }, [open, record]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await updateTransaction(record._id, {
        ...values,
        operationTime: values.operationTime?.toISOString(),
        settleTime: values.settleTime?.toISOString() || null,
      });
      message.success("保存成功");
      onSaved();
      onClose();
    } catch (e) {
      if (e?.errorFields) return; // 校验失败
      message.error("保存失败");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const L = ({ children }) => (
    <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>
      {children}
    </span>
  );

  const sectionTitle = (text) => (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: "#94a3b8",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        margin: "4px 0 10px",
      }}
    >
      {text}
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      title={
        <Space>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: "linear-gradient(135deg, #3b82f6, #6366f1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EditOutlined style={{ color: "#fff", fontSize: 14 }} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
              编辑操作记录
            </div>
            {record && (
              <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>
                {record.asset?.assetNo} ·{" "}
                {OPERATION_CONFIG[record.operation]?.text}
              </div>
            )}
          </div>
        </Space>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            保存
          </Button>
        </div>
      }
      styles={{
        body: { padding: "16px 20px" },
        footer: { padding: "12px 20px" },
      }}
    >
      <Form form={form} layout="vertical" requiredMark={false} size="small">
        {/* 基本信息 */}
        {sectionTitle("基本信息")}
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label={<L>关联资产</L>} name={["asset", "_id"]}>
              <Select disabled options={assetOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<L>操作类型</L>}
              name="operation"
              rules={[{ required: true }]}
            >
              <Select
                options={Object.entries(OPERATION_CONFIG).map(([k, v]) => ({
                  label: v.text,
                  value: k,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<L>操作人</L>}
              name="operator"
              rules={[{ required: true }]}
            >
              <Select
                options={userOptions}
                showSearch
                optionFilterProp="label"
                placeholder="选择操作人"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<L>操作时间</L>}
              name="operationTime"
              rules={[{ required: true }]}
            >
              <DatePicker
                // showTime
                style={{ width: "100%" }}
                format="YYYY-MM-DD HH:mm"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "4px 0 12px" }} />

        {/* 位置变更 */}
        {sectionTitle("位置变更")}
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label={<L>原房间</L>} name="fromRoom">
              <HostSelect allowClear placeholder="原房间" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={<L>新房间</L>} name="toRoom">
              <HostSelect allowClear placeholder="新房间" />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "4px 0 12px" }} />

        {/* 归属变更 */}
        {sectionTitle("归属变更")}
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label={<L>原负责人</L>} name="fromOwner">
              <Select
                allowClear
                placeholder="无"
                showSearch
                optionFilterProp="label"
                options={userOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={<L>新负责人</L>} name="toOwner">
              <Select
                allowClear
                placeholder="无"
                showSearch
                optionFilterProp="label"
                options={userOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "4px 0 12px" }} />

        {/* 结算信息 */}
        {sectionTitle("结算信息")}
        <Row gutter={12} align="middle">
          <Col span={8}>
            <Form.Item
              label={<L>需要结算</L>}
              name="needSettle"
              valuePropName="checked"
            >
              <Switch size="small" onChange={setNeedSettle} />
            </Form.Item>
          </Col>
          {needSettle && (
            <>
              <Col span={8}>
                <Form.Item
                  label={<L>已结算</L>}
                  name="settled"
                  valuePropName="checked"
                >
                  <Switch size="small" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      label={<L>结算金额（元）</L>}
                      name="settleAmount"
                    >
                      <InputNumber
                        min={0}
                        precision={2}
                        prefix="¥"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={<L>结算时间</L>} name="settleTime">
                      <DatePicker
                        style={{ width: "100%" }}
                        format="YYYY-MM-DD"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </>
          )}
        </Row>

        <Divider style={{ margin: "4px 0 12px" }} />
        <Form.Item label={<L>备注</L>} name="remark">
          <TextArea
            rows={2}
            placeholder="操作备注..."
            style={{ resize: "none" }}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

// ─── 主页面 ───────────────────────────────────────────────────────────────────
export default function TransactionListPage() {
  const [filterForm] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [assetOptions, setAssetOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [opCounts, setOpcounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  // 动态查询资产编号列表
  const handleSearchAsset = useMemo(
    () =>
      debounce(async (val) => {
        if (!val) {
          setAssetOptions([]);
          return;
        }

        setFetching(true);
        try {
          const res = (
            await getAssets({
              assetNo: val,
              pageSize: 20,
            })
          ).data;
          console.log("res", res);
          setAssetOptions(
            res?.list?.length
              ? res.list.map((p) => ({
                  label: `${p.assetNo} · ${p.assetName || "-"}`,
                  value: p._id,
                }))
              : [],
          );
        } finally {
          setFetching(false);
        }
      }, 300),
    [],
  );

  // ── 查询 ────────────────────────────────────────────────────────────────────
  const fetchData = useCallback(
    async (page = pagination.current, pageSize = pagination.pageSize) => {
      setLoading(true);
      try {
        const values = filterForm.getFieldsValue();
        const [start, end] = values.operationTimeRange || [];
        const params = {
          page,
          pageSize,
          asset: values.asset || undefined,
          operation: values.operation?.length ? values.operation : undefined,
          operator: values.operator?.length ? values.operator : undefined,
          fromRoom: values.fromRoom || undefined,
          toRoom: values.toRoom || undefined,
          fromOwner: values.fromOwner || undefined,
          toOwner: values.toOwner || undefined,
          needSettle: values.needSettle ?? undefined,
          settled: values.settled ?? undefined,
          operationTimeStart: start
            ? start.startOf("day").toISOString()
            : undefined,
          operationTimeEnd: end ? end.endOf("day").toISOString() : undefined,
        };
        const { data } = (await getTransactions(params)).data;
        console.log("data", data);
        setDataSource(data.list);
        setOpcounts({ ...operation_count_default, ...data.stats });
        console.log("opcounts", opCounts);
        setTotal(data.total);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [filterForm, pagination],
  );

  useEffect(() => {
    fetchData();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const userList = await getUserList();
    setUserOptions(
      userList.data.map((user) => ({
        label: user?.name || user.username,
        value: user._id,
      })),
    );
  };
  const handleSearch = () => {
    setPagination((p) => ({ ...p, current: 1 }));
    fetchData(1, pagination.pageSize);
  };

  const handleReset = () => {
    filterForm.resetFields();
    setPagination((p) => ({ ...p, current: 1 }));
    fetchData(1, pagination.pageSize);
  };

  const handleTableChange = (pag) => {
    const next = { current: pag.current, pageSize: pag.pageSize };
    setPagination(next);
    fetchData(next.current, next.pageSize);
  };

  // ── 列定义 ──────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "交易流水号",
      dataIndex: "transferNo",
      key: "transferNo",
      width: 160,
      fixed: "left",
      render: (v) => (
        <Text
          style={{
            fontFamily: "ui-monospace,SFMono-Regular,monospace",
            fontSize: 11.5,
            color: "#475569",
            letterSpacing: "-0.01em",
          }}
        >
          {v}
        </Text>
      ),
    },
    {
      title: "操作类型",
      dataIndex: "operation",
      key: "operation",
      width: 90,
      fixed: "left",
      render: (v) => <OperationTag operation={v} />,
    },
    {
      title: "资产",
      key: "asset",
      width: 180,
      render: (_, row) =>
        row.asset ? (
          <div>
            <Text
              style={{
                fontFamily: "ui-monospace,monospace",
                fontSize: 11,
                color: "#94a3b8",
                display: "block",
              }}
            >
              {row.asset.assetNo}
            </Text>
            <Text style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>
              {row.asset.assetName}
            </Text>
          </div>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "操作人",
      dataIndex: ["operator", "name"],
      key: "operator",
      width: 80,
      render: (v) =>
        v ? (
          <Space size={4}>
            <Avatar size={18} style={{ background: "#6366f1", fontSize: 9 }}>
              {v[0]}
            </Avatar>
            <Text style={{ fontSize: 12 }}>{v}</Text>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "操作时间",
      dataIndex: "operationTime",
      key: "operationTime",
      width: 130,
      render: (v) => (
        <Text
          style={{
            fontSize: 11,
            color: "#475569",
            fontFamily: "ui-monospace,monospace",
          }}
        >
          {v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "—"}
        </Text>
      ),
    },
    {
      title: "位置变更",
      key: "roomChange",
      width: 160,
      render: (_, row) => (
        <ChangeCell
          from={row.fromRoom?.roomNumber}
          to={row.toRoom?.roomNumber}
          icon={<EnvironmentOutlined />}
        />
      ),
    },
    {
      title: "归属变更",
      key: "ownerChange",
      width: 140,
      render: (_, row) => (
        <ChangeCell
          from={row.fromOwner?.name}
          to={row.toOwner?.name}
          icon={<UserOutlined />}
        />
      ),
    },
    {
      title: "结算状态",
      key: "settle",
      width: 150,
      render: (_, row) => (
        <SettleTag
          needSettle={row.needSettle}
          settled={row.settled}
          amount={row.settleAmount}
        />
      ),
    },
    {
      title: "结算时间",
      dataIndex: "settleTime",
      key: "settleTime",
      width: 100,
      render: (v) =>
        v ? (
          <Text
            style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}
          >
            {dayjs(v).format("YYYY-MM-DD")}
          </Text>
        ) : (
          <Text style={{ color: "#e2e8f0", fontSize: 11 }}>—</Text>
        ),
    },
    {
      title: "备注",
      dataIndex: "remark",
      key: "remark",
      ellipsis: { showTitle: false },
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {v}
            </Text>
          </Tooltip>
        ) : (
          <Text style={{ color: "#e2e8f0" }}>—</Text>
        ),
    },
    {
      title: "创建时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (v) => (
        <Text
          style={{ fontSize: 11, color: "#cbd5e1", fontFamily: "monospace" }}
        >
          {v ? dayjs(v).format("MM-DD HH:mm") : "—"}
        </Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      fixed: "right",
      render: (_, row) => (
        <Tooltip title="编辑">
          <button
            onClick={() => {
              setEditRecord(row);
              setDrawerOpen(true);
            }}
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              border: "1px solid #e2e8f0",
              background: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
            }}
          >
            <EditOutlined style={{ fontSize: 12 }} />
          </button>
        </Tooltip>
      ),
    },
  ];

  // ── 筛选 label 公共样式 ───────────────────────────────────────────────────
  const FL = ({ children }) => (
    <span
      style={{
        fontSize: 11,
        color: "#94a3b8",
        fontWeight: 600,
        letterSpacing: "0.04em",
      }}
    >
      {children}
    </span>
  );

  // ── 操作类型统计 ─────────────────────────────────────────────────────────────
  // const opCounts = Object.keys(OPERATION_CONFIG).reduce((acc, k) => {
  //   acc[k] = dataSource.filter((r) => r.operation === k).length;
  //   return acc;
  // }, {});

  return (
    <div
      style={{
        padding: 24,
        background: "#f8fafc",
        minHeight: "100vh",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      {/* 页头 */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 4,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TransactionOutlined style={{ color: "#fff", fontSize: 15 }} />
          </div>
          <Title
            level={4}
            style={{ margin: 0, color: "#0f172a", fontWeight: 800 }}
          >
            操作记录
          </Title>
        </div>
        <Text style={{ fontSize: 12, color: "#94a3b8" }}>
          资产全生命周期操作流水
        </Text>
      </div>

      {/* 操作类型小卡片 */}
      <Row gutter={10} style={{ marginBottom: 14 }}>
        {Object.entries(OPERATION_CONFIG).map(([k, v]) => (
          <Col key={k} flex="1">
            <div
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                background: "#fff",
                border: `1px solid ${v.border}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  marginBottom: 2,
                }}
              >
                <span style={{ color: v.color, fontSize: 12 }}>{v.icon}</span>
                <span style={{ fontSize: 11, color: v.color, fontWeight: 600 }}>
                  {v.text}
                </span>
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#0f172a",
                  fontFamily: "monospace",
                }}
              >
                {opCounts[k] ?? 0}
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* 筛选区 */}
      <Card
        style={{
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          boxShadow: "none",
          marginBottom: 12,
        }}
        styles={{ body: { padding: "14px 16px" } }}
      >
        <Form
          form={filterForm}
          layout="vertical"
          requiredMark={false}
          size="small"
          onFinish={handleSearch}
        >
          <Row gutter={[10, 0]} align="bottom">
            <Col span={5}>
              <Form.Item
                name="asset"
                label={<FL>资产</FL>}
                style={{ marginBottom: 10 }}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="搜索资产编号/名称"
                  filterOption={false}
                  // optionFilterProp="label"
                  onSearch={handleSearchAsset}
                  options={assetOptions}
                  notFoundContent={fetching ? "搜索中..." : "暂无数据"}
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                name="operation"
                label={<FL>操作类型（多选）</FL>}
                style={{ marginBottom: 10 }}
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="全部类型"
                  maxTagCount={2}
                  options={Object.entries(OPERATION_CONFIG).map(([k, v]) => ({
                    label: v.text,
                    value: k,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={3}>
              <Form.Item
                name="operator"
                label={<FL>操作人（多选）</FL>}
                style={{ marginBottom: 10 }}
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="全部"
                  maxTagCount={1}
                  options={mockUsers.map((u) => ({
                    label: u.name,
                    value: u._id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="operationTimeRange"
                label={<FL>操作时间范围</FL>}
                style={{ marginBottom: 10 }}
              >
                <RangePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>

            <Col span={3}>
              <Form.Item
                name="fromRoom"
                label={<FL>原房间</FL>}
                style={{ marginBottom: 10 }}
              >
                <Select
                  allowClear
                  placeholder="全部"
                  options={mockHosts.map((h) => ({
                    label: h.roomNumber,
                    value: h._id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={3}>
              <Form.Item
                name="toRoom"
                label={<FL>新房间</FL>}
                style={{ marginBottom: 10 }}
              >
                <Select
                  allowClear
                  placeholder="全部"
                  options={mockHosts.map((h) => ({
                    label: h.roomNumber,
                    value: h._id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={3}>
              <Form.Item
                name="fromOwner"
                label={<FL>原负责人</FL>}
                style={{ marginBottom: 10 }}
              >
                <Select
                  allowClear
                  placeholder="全部"
                  options={mockUsers.map((u) => ({
                    label: u.name,
                    value: u._id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={3}>
              <Form.Item
                name="toOwner"
                label={<FL>新负责人</FL>}
                style={{ marginBottom: 10 }}
              >
                <Select
                  allowClear
                  placeholder="全部"
                  options={mockUsers.map((u) => ({
                    label: u.name,
                    value: u._id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={3}>
              <Form.Item
                name="needSettle"
                label={<FL>是否需结算</FL>}
                style={{ marginBottom: 10 }}
              >
                <Select
                  allowClear
                  placeholder="全部"
                  options={[
                    { label: "需结算", value: "true" },
                    { label: "不需要", value: "false" },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={3}>
              <Form.Item
                name="settled"
                label={<FL>结算状态</FL>}
                style={{ marginBottom: 10 }}
              >
                <Select
                  allowClear
                  placeholder="全部"
                  options={[
                    { label: "已结算", value: "true" },
                    { label: "待结算", value: "false" },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
              >
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  重置
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  查询
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* 表格 */}
      <Card
        style={{
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          boxShadow: "none",
        }}
        styles={{ body: { padding: 0 } }}
        title={
          <Space>
            <Text
              style={{
                fontSize: 11,
                color: "#94a3b8",
                fontWeight: 600,
                letterSpacing: "0.05em",
              }}
            >
              操作流水
            </Text>
            <span
              style={{
                fontSize: 11,
                fontFamily: "monospace",
                color: "#64748b",
                background: "#f1f5f9",
                padding: "1px 8px",
                borderRadius: 4,
              }}
            >
              共 {total} 条
            </span>
          </Space>
        }
      >
        <Table
          dataSource={dataSource}
          columns={columns}
          rowKey="_id"
          loading={loading}
          size="small"
          scroll={{ x: 1200 }}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (t) => `共 ${t} 条`,
            size: "small",
          }}
          rowClassName={(_, i) => (i % 2 === 0 ? "" : "tx-stripe")}
          locale={{
            emptyText: (
              <Empty
                imageStyle={{ height: 56 }}
                description={
                  <Text style={{ fontSize: 12, color: "#94a3b8" }}>
                    暂无操作记录
                  </Text>
                }
              />
            ),
          }}
        />
      </Card>

      {/* 编辑 Drawer */}
      <EditDrawer
        open={drawerOpen}
        record={editRecord}
        userOptions={userOptions}
        onClose={() => {
          setDrawerOpen(false);
          setEditRecord(null);
        }}
        onSaved={() => fetchData()}
      />

      <style>{`
        .tx-stripe td { background: #fafbfc !important; }
        .ant-table-tbody > tr:hover > td { background: #f5f3ff !important; }
      `}</style>
    </div>
  );
}
